"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import { useUIStore } from "@/stores/ui-store";
import { useGameStore } from "@/stores/game-store";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X, Send, ChevronDown, Settings } from "lucide-react";

const TEAM_COLORS: Record<string, string> = {
  red: "#c54a4a",
  green: "#5c7935",
  blue: "#6a9bc4",
  default: "#c54a4a",
};

// Tuxemon battle sprites — CC BY-SA 4.0, see /public/assets/tuxemon/LICENSE.md
const MONSTER_SPRITES = ["agnite", "aardart", "abesnaki", "altie", "ambuwl", "allagon"];

function getSurggieSprite(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return MONSTER_SPRITES[Math.abs(h) % MONSTER_SPRITES.length];
}

function SurggieAvatar({ id, team, size = 20 }: { id: string | undefined; team?: string | null; size?: number }) {
  const teamKey = (team ?? "default").toLowerCase();
  const borderColor = TEAM_COLORS[teamKey] ?? TEAM_COLORS.default;
  if (!id) {
    return <div className="rounded-full flex-shrink-0" style={{ width: size, height: size, backgroundColor: borderColor }} />;
  }
  const sprite = getSurggieSprite(id);
  // Sprite sheet is 128×88 with two 64×64 frames side-by-side. Crop frame 0 by
  // sizing the background to (size*2)×(size*88/64) and pinning it to top-left.
  return (
    <div
      data-testid="surggie-avatar"
      className="rounded-full overflow-hidden border-2 flex-shrink-0"
      style={{
        width: size,
        height: size,
        borderColor,
        backgroundImage: `url(/assets/tuxemon/monsters/${sprite}-sheet.png)`,
        backgroundSize: `${size * 2}px ${(size * 88) / 64}px`,
        backgroundPosition: "0 0",
        backgroundRepeat: "no-repeat",
        imageRendering: "pixelated",
      }}
    />
  );
}

const AVAILABLE_MODELS = [
  { value: "claude-4.6", label: "Claude 4.6", provider: "1min.ai" },
  { value: "claude-sonnet-4-20250514", label: "Claude Sonnet 4", provider: "Anthropic" },
  { value: "gpt-4o", label: "GPT-4o", provider: "1min.ai" },
  { value: "gpt-4o-mini", label: "GPT-4o Mini", provider: "1min.ai" },
  { value: "MiniMax-M2.7", label: "MiniMax M2.7", provider: "MiniMax" },
  { value: "openai/gpt-4o-mini", label: "GPT-4o Mini (OR)", provider: "OpenRouter" },
  { value: "meta-llama/llama-3-8b-instruct:free", label: "Llama 3 8B (Free)", provider: "OpenRouter" },
];

export function ChatPanel() {
  const surggies = useGameStore((s) => s.surggies);
  const setSurggies = useGameStore((s) => s.setSurggies);
  const activeSurggieId = useUIStore((s) => s.activeSurggieId);
  const setActiveSurggieId = useUIStore((s) => s.setActiveSurggieId);
  const chatMessages = useUIStore((s) => s.chatMessages);
  const addMessage = useUIStore((s) => s.addChatMessage);
  const setActivePanel = useUIStore((s) => s.setActivePanel);

  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [surggiePickerOpen, setSurggiePickerOpen] = useState(false);
  const [modelPickerOpen, setModelPickerOpen] = useState(false);
  const [updatingModel, setUpdatingModel] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!localStorage.getItem("surggies_welcome_seen")) {
      setShowWelcome(true);
    }
  }, []);

  const dismissWelcome = useCallback(() => {
    setShowWelcome(false);
    if (typeof window !== "undefined") {
      localStorage.setItem("surggies_welcome_seen", "1");
    }
  }, []);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedSurggie = surggies.find((s) => s.id === activeSurggieId);
  const currentMessages = activeSurggieId ? (chatMessages[activeSurggieId] ?? []) : [];

  useEffect(() => {
    if (!activeSurggieId && surggies.length > 0) {
      setActiveSurggieId(surggies[0].id);
    }
  }, [surggies, activeSurggieId, setActiveSurggieId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [currentMessages]);

  useEffect(() => {
    inputRef.current?.focus();
  }, [activeSurggieId]);

  const handleModelChange = useCallback(
    async (model: string) => {
      if (!activeSurggieId) return;
      setUpdatingModel(true);
      setModelPickerOpen(false);

      try {
        const res = await fetch(`/api/surggies/${activeSurggieId}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ llmModel: model }),
        });
        if (res.ok) {
          const data = await res.json();
          setSurggies(
            surggies.map((s) =>
              s.id === activeSurggieId ? { ...s, llmModel: data.surggie?.llmModel ?? model } : s
            )
          );
        }
      } catch (e) {
        console.error("Failed to update model:", e);
      } finally {
        setUpdatingModel(false);
      }
    },
    [activeSurggieId, surggies, setSurggies]
  );

  async function handleSend() {
    if (!input.trim() || sending || !activeSurggieId) return;

    const userMsg = input.trim();
    setInput("");
    addMessage({ role: "user", content: userMsg }, activeSurggieId);
    setSending(true);

    try {
      const res = await fetch("/api/chat", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ surggieId: activeSurggieId, message: userMsg }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        addMessage(
          { role: "assistant", content: `Error: ${err.error ?? res.statusText}` },
          activeSurggieId
        );
        setSending(false);
        return;
      }

      const data = await res.json();
      addMessage(
        {
          role: "assistant",
          content: data.response ?? data.reply ?? "...",
          surggieId: data.surggieId,
          toolCalls: data.toolCalls ?? [],
        },
        activeSurggieId
      );
    } catch {
      addMessage(
        { role: "assistant", content: "Connection lost. Try again." },
        activeSurggieId
      );
    } finally {
      setSending(false);
    }
  }

  const currentModel = AVAILABLE_MODELS.find((m) => m.value === (selectedSurggie?.llmModel ?? "claude-4.6"));

  return (
    <div className="absolute top-0 right-0 bottom-0 w-80 z-50 bg-[#f5ecd6] border-l-2 border-[#1a1a1a] flex flex-col animate-slide-in-right">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b-2 border-[#1a1a1a]">
        <div className="flex items-center gap-2 flex-wrap">
          {/* Surggie picker */}
          <div className="relative">
            <button
              onClick={() => { setSurggiePickerOpen(!surggiePickerOpen); setModelPickerOpen(false); }}
              className="flex items-center gap-2 px-2 py-1 rounded bg-[#fff7e6] border border-[#1a1a1a] hover:border-[#5c4a30] transition-colors cursor-pointer"
            >
              <SurggieAvatar id={selectedSurggie?.id} team={selectedSurggie?.team} size={20} />
              <span className="text-[#1a1a1a] font-mono text-xs font-bold">
                {selectedSurggie?.name ?? "Select"}
              </span>
              <ChevronDown className="w-3 h-3 text-[#5c4a30]" />
            </button>
            {surggiePickerOpen && (
              <div className="absolute top-full left-0 mt-1 w-44 bg-[#fff7e6] border border-[#1a1a1a] rounded-lg shadow-xl z-50">
                {surggies.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => { setActiveSurggieId(s.id); setSurggiePickerOpen(false); }}
                    className={`w-full text-left px-3 py-2 hover:bg-[#b89968]/40 flex items-center gap-2 cursor-pointer ${
                      s.id === activeSurggieId ? "bg-[#b89968]/40" : ""
                    }`}
                  >
                    <SurggieAvatar id={s.id} team={s.team} size={18} />
                    <span className="text-[#1a1a1a] font-mono text-xs">{s.name}</span>
                    <span className="text-[#8a7a5c] font-mono text-xs ml-auto">{s.role}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Model selector */}
          <div className="relative">
            <button
              onClick={() => { setModelPickerOpen(!modelPickerOpen); setSurggiePickerOpen(false); }}
              disabled={updatingModel}
              className="flex items-center gap-1 px-2 py-1 rounded bg-[#fff7e6] border border-[#1a1a1a] hover:border-[#5c4a30] transition-colors cursor-pointer disabled:opacity-50"
            >
              <Settings className="w-3 h-3 text-[#8a7a5c]" />
              <span className="text-[#5c4a30] font-mono text-xs">
                {updatingModel ? "..." : (currentModel?.label ?? selectedSurggie?.llmModel ?? "claude-4.6")}
              </span>
              <ChevronDown className="w-3 h-3 text-[#8a7a5c]" />
            </button>
            {modelPickerOpen && (
              <div className="absolute top-full left-0 mt-1 w-56 bg-[#fff7e6] border border-[#1a1a1a] rounded-lg shadow-xl z-50 max-h-64 overflow-y-auto">
                <div className="px-3 py-2 border-b-2 border-[#1a1a1a]">
                  <span className="text-[#8a7a5c] font-mono text-xs uppercase">Select Model</span>
                </div>
                {AVAILABLE_MODELS.map((m) => (
                  <button
                    key={m.value}
                    onClick={() => handleModelChange(m.value)}
                    className={`w-full text-left px-3 py-2 hover:bg-[#b89968]/40 flex items-center justify-between cursor-pointer ${
                      m.value === (selectedSurggie?.llmModel ?? "claude-4.6") ? "bg-[#b89968]/40" : ""
                    }`}
                  >
                    <div>
                      <span className="text-[#1a1a1a] font-mono text-xs block">{m.label}</span>
                      <span className="text-[#8a7a5c] font-mono text-xs">{m.provider}</span>
                    </div>
                    {m.value === (selectedSurggie?.llmModel ?? "claude-4.6") && (
                      <div className="w-2 h-2 rounded-full bg-[#3d6890]" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <button
          onClick={() => setActivePanel(null)}
          className="p-1 rounded hover:bg-[#b89968]/40 text-[#5c4a30] hover:text-[#1a1a1a] transition-colors cursor-pointer"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 scrollbar-thin">
        {currentMessages.length === 0 && (
          <p className="text-[#8a7a5c] font-mono text-xs text-center mt-8">
            Talk to {selectedSurggie?.name ?? "your Surggies"}...
          </p>
        )}
        {currentMessages.map((msg, i) => (
          <div
            key={i}
            className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`max-w-[85%] p-3 rounded-lg border-2 font-mono text-sm ${
                msg.role === "user"
                  ? "bg-[#c54a4a]/10 border-[#c54a4a]/30 text-[#1a1a1a]"
                  : "bg-[#3d6890]/10 border-[#3d6890]/30 text-[#1a1a1a]"
              }`}
            >
              {msg.role === "assistant" && (
                <div className="flex items-center gap-2 mb-1">
                  <SurggieAvatar id={selectedSurggie?.id} team={selectedSurggie?.team} size={20} />
                  <span className="text-[#3d6890] text-xs font-bold">
                    {selectedSurggie?.name ?? "Surggie"}
                  </span>
                </div>
              )}
              <p className="leading-relaxed">{msg.content}</p>
              {msg.role === "assistant" && msg.toolCalls && msg.toolCalls.length > 0 && (
                <ToolCallList calls={msg.toolCalls} msgIdx={i} />
              )}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="bg-[#3d6890]/10 border-2 border-[#3d6890]/30 p-3 rounded-lg">
              <div className="flex gap-1">
                <span className="w-2 h-2 rounded-full bg-[#3d6890] animate-bounce" />
                <span className="w-2 h-2 rounded-full bg-[#3d6890] animate-bounce [animation-delay:0.1s]" />
                <span className="w-2 h-2 rounded-full bg-[#3d6890] animate-bounce [animation-delay:0.2s]" />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="p-4 border-t-2 border-[#1a1a1a]">
        {showWelcome && currentMessages.length === 0 && (
          <div className="mb-3 p-3 rounded-lg bg-[#fff7e6] border-2 border-[#1a1a1a] relative">
            <button
              type="button"
              onClick={dismissWelcome}
              className="absolute top-1 right-1 p-0.5 text-[#8a7a5c] hover:text-[#1a1a1a] cursor-pointer"
              title="Dismiss"
            >
              <X className="w-3 h-3" />
            </button>
            <div className="text-[#c54a4a] font-mono text-xs font-bold mb-1">👋 Welcome, CEO</div>
            <p className="text-[#1a1a1a] font-mono text-[11px] leading-relaxed">
              Your <span className="text-[#3d6890]">Surggies</span> can search the web, remember things across sessions, run code, and delegate to each other. Tap a chip below to try one.
            </p>
          </div>
        )}
        {currentMessages.length === 0 && !sending && (
          <div className="flex flex-wrap gap-1.5 mb-2">
            {SUGGESTION_CHIPS.map((chip) => (
              <button
                key={chip.label}
                type="button"
                onClick={() => setInput(chip.prompt)}
                className="text-[10px] px-2 py-1 rounded-full bg-[#3d6890]/10 border border-[#3d6890]/30 text-[#3d6890]/90 hover:bg-[#3d6890]/20 hover:border-[#3d6890]/60 transition-colors cursor-pointer font-mono"
                title={chip.prompt}
              >
                {chip.label}
              </button>
            ))}
          </div>
        )}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSend();
          }}
          className="flex gap-2"
        >
          <Input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={`Chat with ${selectedSurggie?.name ?? "Surggie"}...`}
            className="flex-1"
          />
          <Button
            type="submit"
            size="sm"
            variant="secondary"
            disabled={sending || !input.trim()}
          >
            <Send className="w-4 h-4" />
          </Button>
        </form>
      </div>
    </div>
  );
}

type ToolCall = { name: string; params?: Record<string, unknown>; result?: unknown };

function ToolCallList({ calls, msgIdx }: { calls: ToolCall[]; msgIdx: number }) {
  const [expanded, setExpanded] = useState<number | null>(null);
  return (
    <div className="mt-2 space-y-1">
      <div className="flex flex-wrap gap-1">
        {calls.map((tc, i) => {
          const result = tc.result as { worker_name?: string; status?: string } | undefined;
          const isDelegation = tc.name === "delegate_to" || tc.name === "delegate_to_async";
          const workerName = result?.worker_name;
          const isOpen = expanded === i;
          if (isDelegation && workerName) {
            const queued = result?.status === "queued";
            return (
              <button
                key={`${msgIdx}-${i}`}
                type="button"
                onClick={() => setExpanded(isOpen ? null : i)}
                className={`text-[10px] px-1.5 py-0.5 rounded font-mono cursor-pointer transition-colors border ${
                  isOpen
                    ? "bg-[#c54a4a]/40 border-[#c54a4a] text-[#1a1a1a]"
                    : "bg-[#c54a4a]/20 border-[#c54a4a]/50 text-[#c54a4a] hover:bg-[#c54a4a]/30"
                }`}
              >
                🤖 → {workerName}{queued ? " (queued)" : ""}
              </button>
            );
          }
          return (
            <button
              key={`${msgIdx}-${i}`}
              type="button"
              onClick={() => setExpanded(isOpen ? null : i)}
              className={`text-[10px] px-1.5 py-0.5 rounded font-mono cursor-pointer transition-colors border ${
                isOpen
                  ? "bg-[#a04ab8]/40 border-[#a04ab8] text-[#1a1a1a]"
                  : "bg-[#a04ab8]/20 border-[#a04ab8]/40 text-[#a04ab8] hover:bg-[#a04ab8]/30"
              }`}
            >
              ⚡ {tc.name}
            </button>
          );
        })}
      </div>
      {expanded !== null && (
        <ToolResultCard call={calls[expanded]} />
      )}
    </div>
  );
}

function ToolResultCard({ call }: { call: ToolCall }) {
  const result = call.result;
  if (!result) {
    return <div className="text-[10px] text-[#8a7a5c] italic">No result captured.</div>;
  }
  // web_search
  if (call.name === "web_search" && typeof result === "object" && Array.isArray((result as { results?: unknown[] }).results)) {
    const results = (result as { results: { title: string; url: string; snippet: string }[] }).results;
    return (
      <div className="bg-[#fff7e6] rounded p-2 space-y-1.5 border border-[#1a1a1a]">
        {results.slice(0, 5).map((r, i) => (
          <div key={i} className="text-[10px]">
            <a href={r.url} target="_blank" rel="noreferrer" className="text-[#3d6890] hover:underline font-bold block truncate">{r.title}</a>
            <div className="text-[#8a7a5c] truncate">{r.url}</div>
            <div className="text-[#5c4a30]">{r.snippet}</div>
          </div>
        ))}
      </div>
    );
  }
  // web_fetch
  if (call.name === "web_fetch" && typeof result === "object") {
    const r = result as { url?: string; text?: string; error?: string };
    return (
      <div className="bg-[#fff7e6] rounded p-2 border border-[#1a1a1a] text-[10px] space-y-1">
        {r.url && <div className="text-[#3d6890] truncate">{r.url}</div>}
        {r.error && <div className="text-[#c54a4a]">Error: {r.error}</div>}
        {r.text && <div className="text-[#5c4a30] whitespace-pre-wrap line-clamp-6">{r.text.slice(0, 600)}</div>}
      </div>
    );
  }
  // wiki_read
  if (call.name === "wiki_read" && typeof result === "object") {
    const r = result as { slug?: string; text?: string; results?: string[]; error?: string };
    if (r.error) return <div className="bg-[#fff7e6] rounded p-2 text-[10px] text-[#c54a4a] border border-[#1a1a1a]">{r.error}</div>;
    if (r.results) {
      return (
        <div className="bg-[#fff7e6] rounded p-2 border border-[#1a1a1a] text-[10px] space-y-0.5">
          <div className="text-[#8a7a5c] mb-1">Found {r.results.length} matching pages:</div>
          {r.results.slice(0, 8).map((slug, i) => (
            <div key={i} className="text-[#3d6890]">📄 {slug}</div>
          ))}
        </div>
      );
    }
    return (
      <div className="bg-[#fff7e6] rounded p-2 border border-[#1a1a1a] text-[10px] space-y-1">
        <div className="text-[#3d6890] font-bold">📄 {r.slug}</div>
        <div className="text-[#5c4a30] whitespace-pre-wrap line-clamp-8">{r.text}</div>
      </div>
    );
  }
  // wiki_write
  if (call.name === "wiki_write" && typeof result === "object") {
    const r = result as { slug?: string; title?: string; success?: boolean };
    return (
      <div className="bg-[#fff7e6] rounded p-2 border border-[#1a1a1a] text-[10px]">
        <span className="text-[#5c7935]">✓</span> Saved <span className="text-[#3d6890] font-bold">{r.slug}</span>
        {r.title && <span className="text-[#5c4a30]"> — {r.title}</span>}
      </div>
    );
  }
  // delegate_to (sync) — show the worker's response
  if ((call.name === "delegate_to" || call.name === "delegate_to_async") && typeof result === "object") {
    const r = result as { worker_name?: string; worker_role?: string; response?: string; message?: string };
    return (
      <div className="bg-[#fff7e6] rounded p-2 border border-[#c54a4a]/30 text-[10px] space-y-1">
        <div className="text-[#c54a4a] font-bold">🤖 {r.worker_name} <span className="text-[#8a7a5c] font-normal">({r.worker_role})</span></div>
        <div className="text-[#1a1a1a] whitespace-pre-wrap line-clamp-10">{r.response ?? r.message}</div>
      </div>
    );
  }
  // Generic fallback — pretty-printed JSON
  return (
    <pre className="bg-[#fff7e6] rounded p-2 border border-[#1a1a1a] text-[10px] text-[#5c4a30] overflow-x-auto whitespace-pre-wrap line-clamp-8">
      {typeof result === "string" ? result : JSON.stringify(result, null, 2)}
    </pre>
  );
}

const SUGGESTION_CHIPS: { label: string; prompt: string }[] = [
  { label: "🔍 Search the web", prompt: "Search the web for the latest AI agent frameworks and summarize the top 3." },
  { label: "🧠 Remember this", prompt: "Save a memory: I prefer concise responses and want updates only when something ships." },
  { label: "🌐 Fetch a URL", prompt: "Fetch https://news.ycombinator.com and tell me the top 5 stories." },
  { label: "🧮 Run code", prompt: "Use execute_code to compute the first 10 Fibonacci numbers." },
  { label: "📚 Read your memory", prompt: "Search your wiki memory for anything about my preferences." },
];
